/**
 * Phase 4 tool confirmation registry.
 *
 * Maps a tool name (as defined in server/src/services/ai-tools/*) to the
 * React component rendered in the Agent page's visualisation panel when
 * that tool's call is in `needs_confirmation` state. Each component is a
 * thin shell over existing UI (settings panel, template editor, etc.)
 * pre-filled from the tool-use input block.
 *
 * Adding a tool:
 *   1. Add an entry to `toolRegistry` below
 *   2. Create a component with signature ToolConfirmationProps
 *   3. Component calls `onConfirm(modifiedInput)` or `onReject()`
 *
 * When the tool name has no registered component, the Agent page falls
 * back to a generic JSON-preview + confirm/reject pair.
 */

import { type FC } from 'react';
import { PauseCampaignTool } from './tools/PauseCampaignTool';
import { EditCampaignSettingsTool } from './tools/EditCampaignSettingsTool';
import { EditCampaignICPTool } from './tools/EditCampaignICPTool';
import { RewriteEmailStepTool } from './tools/RewriteEmailStepTool';
import { RunProspectingBatchTool } from './tools/RunProspectingBatchTool';

export interface ToolConfirmationProps {
  /** Raw input payload the agent proposed. */
  input: Record<string, unknown>;
  /** Human-readable tool description from the server's Anthropic.Tool def. */
  description?: string;
  /** Submit the (possibly edited) input back to the server to run the tool. */
  onConfirm: (modifiedInput: Record<string, unknown>) => void;
  /** Cancel this tool call. */
  onReject: () => void;
}

export const toolRegistry: Record<string, FC<ToolConfirmationProps>> = {
  pause_campaign: PauseCampaignTool,
  edit_campaign_settings: EditCampaignSettingsTool,
  edit_campaign_icp: EditCampaignICPTool,
  rewrite_email_step: RewriteEmailStepTool,
  run_prospecting_batch: RunProspectingBatchTool,
};

export function getToolComponent(toolName: string): FC<ToolConfirmationProps> | null {
  return toolRegistry[toolName] ?? null;
}
